// src/pinball/components/PinballGame.tsx - Main game container component

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PinballEngine } from '../PinballEngine';
import { InputHandler } from '../InputHandler';
import { PinballSoundManager } from '../PinballSoundManager';
import {
  TableThemeConfig,
  PinballGameState,
  PinballGameStatus,
  VisualEffect,
  HighScoreEntry,
  CollisionEvent,
} from '../types';
import { ALL_TABLES } from '../tables';
import { PinballBoard } from './PinballBoard';
import { PinballHUD } from './PinballHUD';
import { TableSelector } from './TableSelector';
import { HighScoreTable } from './HighScoreTable';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { UniversalGameLayout, UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import { GameSessionManager } from '../../services/GameSessionManager';
import './PinballGame.css';

interface PinballGameProps {
  onBack?: () => void;
}

export const PinballGame: React.FC<PinballGameProps> = ({ onBack }) => {
  const [gameStatus, setGameStatus] = useState<'mode_select' | 'table_select' | 'playing' | 'paused' | 'game_over'>('mode_select');
  const [gameState, setGameState] = useState<PinballGameState | null>(null);
  const [selectedTable, setSelectedTable] = useState<TableThemeConfig | null>(null);
  const [effects, setEffects] = useState<VisualEffect[]>([]);
  const [highScores, setHighScores] = useState<HighScoreEntry[]>([]);
  const [showHighScoreEntry, setShowHighScoreEntry] = useState(false);
  const [finalScore, setFinalScore] = useState(0);

  const engineRef = useRef<PinballEngine | null>(null);
  const inputRef = useRef<InputHandler | null>(null);
  const soundRef = useRef<PinballSoundManager | null>(null);
  const animFrameRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  const gameSessionIdRef = useRef<string | null>(null);
  const gameContainerRef = useRef<HTMLDivElement>(null);
  const gameStatusRef = useRef(gameStatus);

  // Load high scores from localStorage
  useEffect(() => {
    loadHighScores();
  }, []);

  // Auto-pause on tab visibility change
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden && gameStatus === 'playing') {
        handlePause();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [gameStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
      inputRef.current?.destroy();
      if (gameSessionIdRef.current) {
        GameSessionManager.getInstance().endGameSession(gameSessionIdRef.current, false);
      }
    };
  }, []);

  // Auto-focus the game container when playing
  useEffect(() => {
    if (gameStatus === 'playing' && gameContainerRef.current) {
      gameContainerRef.current.focus();
    }
    gameStatusRef.current = gameStatus;
  }, [gameStatus]);

  const loadHighScores = () => {
    try {
      const stored = localStorage.getItem('pinball_high_scores');
      if (stored) {
        setHighScores(JSON.parse(stored));
      }
    } catch {
      // Ignore parse errors
    }
  };

  const saveHighScores = (scores: HighScoreEntry[]) => {
    setHighScores(scores);
    localStorage.setItem('pinball_high_scores', JSON.stringify(scores));
  };

  const getHighScoreForTable = (tableId: string): number => {
    const tableScores = highScores.filter(s => s.tableTheme === tableId);
    return tableScores.length > 0 ? Math.max(...tableScores.map(s => s.score)) : 0;
  };

  const handleStartGame = () => {
    setGameStatus('table_select');
  };

  const handleSelectTable = (table: TableThemeConfig) => {
    setSelectedTable(table);
    startGame(table);
  };

  const startGame = (table: TableThemeConfig) => {
    // Initialize engine
    const engine = new PinballEngine(table);
    engine.initialize();
    engineRef.current = engine;

    // Initialize sound
    const sound = new PinballSoundManager();
    soundRef.current = sound;

    // Set up callbacks
    engine.onScoreChange((score) => {
      // Score updates handled in game loop
    });

    engine.onBallDrain((remaining) => {
      sound.play('drain');
    });

    engine.onGameOver((score) => {
      handleGameOver(score);
    });

    engine.onCollision((event) => {
      handleCollisionEffect(event);
    });

    // Start game
    engine.startGame();
    setGameStatus('playing');
    gameStatusRef.current = 'playing'; // Set ref immediately for game loop
    setGameState(engine.getState());

    // Start game session tracking
    gameSessionIdRef.current = GameSessionManager.getInstance().startGameSession('pinball');

    // Start game loop
    lastTimeRef.current = performance.now();
    animFrameRef.current = requestAnimationFrame(gameLoop);
  };

  const gameLoop = (timestamp: number) => {
    if (!engineRef.current) return;

    const dt = Math.min((timestamp - lastTimeRef.current) / 1000, 1 / 30);
    lastTimeRef.current = timestamp;

    if (gameStatusRef.current === 'playing') {
      engineRef.current.update(dt);
      const state = engineRef.current.getState();
      setGameState(state);

      // Clean up expired effects
      setEffects(prev => prev.filter(e => Date.now() - e.startTime < e.duration));

      if (state.status === 'game_over') {
        handleGameOver(state.score);
        return;
      }
    }

    animFrameRef.current = requestAnimationFrame(gameLoop);
  };

  const handleCollisionEffect = (event: CollisionEvent) => {
    const sound = soundRef.current;
    if (!sound) return;

    // Play sound based on element type
    switch (event.elementType) {
      case 'bumper':
        sound.play('bumper_hit');
        break;
      case 'pop_bumper':
        sound.play('pop_bumper_hit');
        break;
      case 'target':
        sound.play('target_hit');
        break;
      case 'drop_target':
        sound.play('drop_target_hit');
        break;
      case 'slingshot':
        sound.play('slingshot_hit');
        break;
      case 'flipper':
        // Don't play sound for every flipper contact
        break;
      default:
        break;
    }

    // Add visual effect
    if (event.elementType !== 'wall' && event.elementType !== 'flipper') {
      setEffects(prev => [
        ...prev,
        {
          type: 'flash',
          position: event.contactPoint,
          startTime: Date.now(),
          duration: 200,
        },
      ]);
    }
  };

  const handlePause = () => {
    if (engineRef.current && gameStatus === 'playing') {
      engineRef.current.pause();
      setGameStatus('paused');
      gameStatusRef.current = 'paused';
    }
  };

  const handleResume = () => {
    if (engineRef.current && gameStatus === 'paused') {
      engineRef.current.resume();
      setGameStatus('playing');
      gameStatusRef.current = 'playing';
      lastTimeRef.current = performance.now();
      animFrameRef.current = requestAnimationFrame(gameLoop);
    }
  };

  const handleGameOver = (score: number) => {
    setGameStatus('game_over');
    setFinalScore(score);

    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
    }

    // End game session
    if (gameSessionIdRef.current) {
      GameSessionManager.getInstance().endGameSession(gameSessionIdRef.current, true);
      gameSessionIdRef.current = null;
    }

    // Check if qualifies for high score
    if (selectedTable) {
      const tableScores = highScores.filter(s => s.tableTheme === selectedTable.id);
      if (tableScores.length < 10 || score > Math.min(...tableScores.map(s => s.score))) {
        setShowHighScoreEntry(true);
      }
    }
  };

  const handleInitialsSubmit = (initials: string) => {
    if (!selectedTable) return;

    const newEntry: HighScoreEntry = {
      initials,
      score: finalScore,
      tableTheme: selectedTable.id,
      date: Date.now(),
    };

    const updatedScores = [...highScores, newEntry]
      .sort((a, b) => b.score - a.score)
      .slice(0, 50); // Keep top 50 across all tables

    saveHighScores(updatedScores);
    setShowHighScoreEntry(false);
  };

  const handleNewGame = () => {
    setGameStatus('table_select');
    setGameState(null);
    setEffects([]);
    setShowHighScoreEntry(false);
    engineRef.current = null;
  };

  const handleCanvasReady = useCallback((canvas: HTMLCanvasElement) => {
    // Initialize input handler
    const input = new InputHandler();
    input.initialize(canvas);
    input.setCallbacks({
      onFlipperActivate: (side) => {
        engineRef.current?.activateFlipper(side);
        soundRef.current?.play('flipper_activate');
      },
      onFlipperRelease: (side) => {
        engineRef.current?.releaseFlipper(side);
        soundRef.current?.play('flipper_release');
      },
      onPlungerCharge: () => {
        engineRef.current?.chargePlunger();
      },
      onPlungerRelease: () => {
        engineRef.current?.releasePlunger();
        soundRef.current?.play('plunger_release');
      },
      onPause: () => {
        if (gameStatusRef.current === 'playing') {
          handlePause();
        } else if (gameStatusRef.current === 'paused') {
          handleResume();
        }
      },
      onResetBall: () => {
        try {
          const engine = engineRef.current;
          if (engine) {
            const bodies = engine.getPhysics().getBodies();
            const ball = bodies.find((b: any) => b.isActive);
            if (ball) {
              const pos = engine.getPlungerPosition();
              ball.position = { x: pos.x, y: pos.y - 20 };
              ball.velocity = { x: 0, y: 0 };
              engine.setBallInLauncher(true);
            }
          }
        } catch (e) {
          console.warn('Reset ball failed:', e);
        }
      },
    });
    inputRef.current = input;
  }, []);

  // Render based on game status
  const renderContent = () => {
    switch (gameStatus) {
      case 'mode_select':
        const pinballConfig: GameModeConfig = {
          gameName: 'Pinball',
          gameIcon: '🎰',
          availableModes: ['ai'],
          difficulties: [],
        };

        return (
          <UniversalGameModeSelector
            config={pinballConfig}
            onStart={() => handleStartGame()}
            isAuthenticated={true}
          />
        );

      case 'table_select':
        return (
          <TableSelector
            onSelectTable={handleSelectTable}
            onBack={() => setGameStatus('mode_select')}
          />
        );

      case 'playing':
      case 'paused': {
        const layoutConfig: UniversalGameLayoutConfig = {
          gameName: 'Pinball',
          fullWidth: true,
          board: (
            <div className="pinball-game-main">
              {selectedTable && gameState && (
                <PinballBoard
                  tableConfig={selectedTable}
                  state={gameState}
                  effects={effects}
                  onCanvasReady={handleCanvasReady}
                />
              )}
              {gameStatus === 'paused' && (
                <div className="pinball-pause-overlay" data-testid="pause-overlay">
                  <div className="pause-content">
                    <h2>PAUSED</h2>
                    <button onClick={handleResume} className="resume-button">
                      Resume
                    </button>
                    <button onClick={handleNewGame} className="quit-button">
                      Quit
                    </button>
                  </div>
                </div>
              )}
            </div>
          ),
          rightPanels: [
            {
              id: 'hud',
              title: 'Score',
              icon: '🎯',
              content: gameState && selectedTable ? (
                <PinballHUD
                  state={gameState}
                  highScore={getHighScoreForTable(selectedTable.id)}
                />
              ) : <div />,
            },
            {
              id: 'controls',
              title: 'Controls',
              icon: '🎮',
              content: (
                <div className="pinball-controls-legend" data-testid="controls-legend">
                  <div className="controls-legend-section">
                    <div className="controls-legend-row">
                      <span className="controls-keys">
                        <kbd>←</kbd> <kbd>Z</kbd>
                      </span>
                      <span className="controls-action">Left Flipper</span>
                    </div>
                    <div className="controls-legend-row">
                      <span className="controls-keys">
                        <kbd>→</kbd> <kbd>/</kbd>
                      </span>
                      <span className="controls-action">Right Flipper</span>
                    </div>
                    <div className="controls-legend-row">
                      <span className="controls-keys">
                        <kbd>Space</kbd> <kbd>↓</kbd>
                      </span>
                      <span className="controls-action">Launch Ball</span>
                    </div>
                    <div className="controls-legend-row">
                      <span className="controls-keys">
                        <kbd>Esc</kbd>
                      </span>
                      <span className="controls-action">Pause</span>
                    </div>
                    <div className="controls-legend-row">
                      <span className="controls-keys">
                        <kbd>R</kbd>
                      </span>
                      <span className="controls-action">Reset Ball</span>
                    </div>
                  </div>
                </div>
              ),
            },
          ],
          statusBar: {
            turnIndicator: <span>{gameStatus === 'paused' ? '⏸ Paused' : '🎰 Playing'}</span>,
            gameStatusType: 'active',
          },
          actions: [
            { id: 'pause', label: gameStatus === 'paused' ? 'Resume' : 'Pause', icon: gameStatus === 'paused' ? '▶️' : '⏸', onClick: gameStatus === 'paused' ? handleResume : handlePause, variant: 'secondary' },
            { id: 'quit', label: 'Quit', icon: '🚪', onClick: handleNewGame, variant: 'danger' },
          ],
        };

        return <UniversalGameLayout config={layoutConfig} />;
      }

      case 'game_over': {
        const layoutConfig: UniversalGameLayoutConfig = {
          gameName: 'Pinball',
          fullWidth: true,
          board: (
            <div className="pinball-game-over" data-testid="game-over-screen">
              <h2>GAME OVER</h2>
              <div className="final-score">
                <span className="final-score-label">Final Score</span>
                <span className="final-score-value">{finalScore.toLocaleString()}</span>
              </div>

              <HighScoreTable
                scores={highScores.filter(s => s.tableTheme === selectedTable?.id)}
                currentScore={finalScore}
                onInitialsSubmit={handleInitialsSubmit}
                showInitialsPrompt={showHighScoreEntry}
              />
            </div>
          ),
          statusBar: {
            turnIndicator: <span>Game Over — Score: {finalScore.toLocaleString()}</span>,
            gameStatus: 'Game Over',
            gameStatusType: 'ended',
          },
          actions: [
            { id: 'play-again', label: 'Play Again', icon: '🔄', onClick: handleNewGame, variant: 'primary' },
            { id: 'back', label: 'Back to Hub', icon: '🚪', onClick: onBack || handleNewGame, variant: 'secondary' },
          ],
          gameOver: {
            show: true,
            result: 'loss',
            title: 'GAME OVER',
            subtitle: `Final Score: ${finalScore.toLocaleString()}`,
            icon: '🎰',
            onPlayAgain: handleNewGame,
            onExit: onBack || handleNewGame,
          },
        };

        return <UniversalGameLayout config={layoutConfig} />;
      }
    }
  };

  return (
    <div
      className="pinball-game"
      data-testid="pinball-game"
      ref={gameContainerRef}
      tabIndex={0}
    >
      {renderContent()}
    </div>
  );
};
